import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Loading, SectionTitle, EmptyState } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { LocationPicker } from '@/components/LocationPicker';
import { MapPinned, MapPin, Search, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/*
 * AdminAddressPins — cleanup workspace for delivery locations that are missing a
 * map pin (imported customers start at 0,0). Setting a pin fixes distance display
 * and delivery-charge computation for every future order from that location, and
 * refreshes any pending orders already placed against it.
 */
export default function AdminAddressPins() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [row, setRow] = useState(null);       // row being pinned
  const [pin, setPin] = useState({ lat: 0, lng: 0, address: '' });
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/customers/pins/missing').then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const list = data.items.filter((r) => !q || (r.company_name + ' ' + r.address).toLowerCase().includes(q.toLowerCase()));

  const openPin = (r) => {
    setRow(r);
    setPin({ lat: 0, lng: 0, address: r.address || '' });
  };

  const savePin = async () => {
    if (!pin.lat || !pin.lng) return toast.error('Drop a pin on the map first (search or click the map)');
    setSaving(true);
    try {
      const res = await api.put(`/customers/${row.customer_id}/addresses/${row.address_id}/pin`, pin);
      const extra = res.data.pending_orders_updated ? ` · ${res.data.pending_orders_updated} pending order(s) refreshed` : '';
      toast.success(`Pin set for ${row.company_name} — ${res.data.distance_km} km from depot${extra}`);
      setRow(null);
      setData((d) => ({ count: d.count - 1, items: d.items.filter((i) => i.address_id !== row.address_id) }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the pin'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <SectionTitle>Address Map Pins</SectionTitle>
      <div className="rounded-xl border border-border bg-card p-4 flex items-start gap-3">
        <MapPinned className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="font-medium">{data.count === 0 ? 'All delivery locations have map pins.' : `${data.count} delivery location${data.count === 1 ? '' : 's'} missing a map pin`}</p>
          <p className="text-muted-foreground text-xs mt-0.5">Orders from a location without a pin can't show true distance. Click "Set Pin", search the address (pre-filled) or click on the map, and save. Pending orders are refreshed automatically.</p>
        </div>
      </div>

      {data.count > 0 && (
        <>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company or address" className="pl-9" data-testid="pins-search-input" />
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60"><tr className="text-left"><th className="p-3">Company</th><th className="p-3">Location</th><th className="p-3">Address</th><th className="p-3 w-28"></th></tr></thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.address_id} className="border-t border-border" data-testid={`pin-row-${r.address_id}`}>
                    <td className="p-3 font-medium max-w-[220px] truncate">{r.company_name}</td>
                    <td className="p-3 text-xs">{r.label}{r.is_default && <span className="text-primary ml-1">(Default)</span>}</td>
                    <td className="p-3 text-xs text-muted-foreground max-w-[420px] truncate">{r.address || '—'}</td>
                    <td className="p-3">
                      <Button size="sm" onClick={() => openPin(r)} data-testid={`set-pin-${r.address_id}`}><MapPin className="h-4 w-4 mr-1" />Set Pin</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.length === 0 && <div className="p-6"><EmptyState title="No matches" icon={Search} /></div>}
          </div>
        </>
      )}
      {data.count === 0 && (
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
          <p className="font-medium">All locations pinned</p>
          <p className="text-sm text-muted-foreground">Every order will now show the true distance from the depot.</p>
        </div>
      )}

      <Dialog open={!!row} onOpenChange={(o) => { if (!o) setRow(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Set Map Pin — {row?.company_name}</DialogTitle></DialogHeader>
          {row && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground -mt-1">{row.label} · Search below is pre-filled with the saved address — pick a result or click the map to drop the pin.</p>
              <LocationPicker
                value={{ address: pin.address, lat: pin.lat, lng: pin.lng }}
                onChange={(n) => setPin((p) => ({ ...p, ...n }))}
                initialQuery={row.address || row.company_name}
                testId="pin-location-picker"
              />
              <div>
                <Label className="text-xs">Address text (saved with the pin)</Label>
                <Textarea rows={2} value={pin.address} onChange={(e) => setPin({ ...pin, address: e.target.value })} data-testid="pin-address-input" />
              </div>
              {pin.lat && pin.lng ? (
                <p className="text-xs text-emerald-600 dark:text-emerald-400" data-testid="pin-coords-set">Pin set: {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}</p>
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="pin-coords-missing">No pin yet — search or click on the map.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={savePin} disabled={saving || !pin.lat || !pin.lng} data-testid="save-pin-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Pin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
